import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DynamicHistory } from "../dynamicHistory";
import { logUnhandled } from "../api/logError";
import { PageSpinner } from "../shared/Page";
import type { OptionCategoryHistoryEntry } from "./types";

type OptionCategoryHistoryProps = {
  entries: Array<OptionCategoryHistoryEntry> | undefined;
  nameHeaderKey: TranslationKey;
};

export const OptionCategoryHistory = ({ entries, nameHeaderKey }: OptionCategoryHistoryProps) => {
  const { t } = useTranslation();

  if (!entries) return <PageSpinner />;

  return (
    <DynamicHistory.Entries
      historyEntries={entries}
      renderCustomEntry={entry => {
        switch (entry.customType) {
          case "categoryCreated":
            return (
              <Stack spacing={1}>
                <DynamicHistory.Metadata
                  {...entry}
                  actionLabel={t("OptionCategoryList.History.Added")}
                  header={entry.newValue?.label}
                />
                {entry.newValue?.description && (
                  <Typography
                    sx={{
                      fontSize: theme => theme.typography.pxToRem(13),
                      color: theme => theme.palette.text.secondary,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {entry.newValue.description}
                  </Typography>
                )}
              </Stack>
            );
          case "categoryName":
            return (
              <DynamicHistory.BeforeAndAfterEntry
                {...entry}
                header={t(nameHeaderKey)}
                before={<DynamicHistory.SnapshotValueText>{entry.oldValue}</DynamicHistory.SnapshotValueText>}
                after={<DynamicHistory.SnapshotValueText>{entry.newValue}</DynamicHistory.SnapshotValueText>}
              />
            );
          case "categoryDescription":
            return (
              <DynamicHistory.BeforeAndAfterEntry
                {...entry}
                header={t("OptionCategoryList.History.DescriptionOf", { name: entry.newValue.label })}
                before={<OptionCategoryDescriptionValue description={entry.oldValue.description} />}
                after={<OptionCategoryDescriptionValue description={entry.newValue.description} />}
              />
            );
          case "categoryDeleted":
            return <DynamicHistory.Metadata {...entry} header={entry.oldValue?.label} />;
          default:
            return logUnhandled(entry, "option category history entry");
        }
      }}
    />
  );
};

const OptionCategoryDescriptionValue = ({ description }: { description: string | null }) =>
  description ? (
    <DynamicHistory.SnapshotValueText>{description}</DynamicHistory.SnapshotValueText>
  ) : (
    <DynamicHistory.EmptyValue />
  );
